import type { EstimatedCalculation__ItemFragment } from "../../doc-items/boxes/EstimatedCalculationBox.generated.js";
import type { DocDataGrid_ItemFragment } from "../DocDataGrid.generated.js";
import type { DocDataGridRow, PendingCellEdit } from "../types.js";

type ProposedCalculation = Omit<
  NonNullable<EstimatedCalculation__ItemFragment["proposedCalculation"]>,
  "__typename"
>;

/**
 * Creates a valueSetter that:
 * 1. Optimistically updates `item.proposedCalculation[calculationField]` so
 *    valueGetter/renderCell immediately reflects the new value after editing.
 * 2. Attaches `_pendingEdit.apply` so processRowUpdate can fire the mutation.
 *
 * Without this, processRowUpdate returns a row with a stale proposedCalculation
 * and the cell appears stuck at the old value.
 *
 * Note: the `calculationField` is the key in `proposedCalculation` (ItemCalculationFragment),
 * which may differ from the mutation input field name — e.g. estimatedQuantity→quantity,
 * estimatedTime→timePerUnit, materialBuyingPrice→materialBuyingPricePerUnit.
 *
 * Curried so `T` can be provided explicitly while `K` is inferred from `calculationField`:
 *   makeCalculationValueSetter<T>()("materialBuyingPricePerUnit", applyFn)
 */
export function makeCalculationValueSetter<
  T extends DocDataGrid_ItemFragment & EstimatedCalculation__ItemFragment,
>() {
  return <K extends keyof ProposedCalculation>(
    _calculationField: K,
    applyFn: (itemId: string, value: ProposedCalculation[K]) => Promise<void>
  ): ((
    value: ProposedCalculation[K],
    row: DocDataGridRow<T> & {
      _pendingEdit?: PendingCellEdit<EstimatedCalculation__ItemFragment>;
    }
  ) => DocDataGridRow<T> & {
    _pendingEdit?: PendingCellEdit<EstimatedCalculation__ItemFragment>;
  }) => {
    return (value, row) => ({
      ...row,
      _pendingEdit: {
        apply: (itemId: string) => applyFn(itemId, value),
      },
    });
  };
}
